import React, { useState, useEffect } from 'react';
import { Bell, X, TrendingDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function AlertsNotification() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    loadAlerts();
    const interval = setInterval(loadAlerts, 300000);
    return () => clearInterval(interval);
  }, []);

  const loadAlerts = async () => {
    const token = sessionStorage.getItem('consumer_session_token');
    if (!token) return;

    try {
      const response = await axios.get(`${API_BASE_URL}/api/consumer/watchlist/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(response.data.alerts || []);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    }
  };

  const unreadCount = alerts.length;

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-gray-600 hover:text-blue-600 transition-colors"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="absolute right-0 top-12 w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50">
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Price Alerts</h3>
            <button onClick={() => setShowDropdown(false)} className="p-1 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {alerts.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-sm text-gray-600">No active alerts</p>
                <p className="text-xs text-gray-500 mt-1">Add deals to watchlist to get price alerts</p>
              </div>
            ) : (
              alerts.map((alert) => {
                const isPriceDown = alert.price_change < 0;
                const isPriceUp = alert.price_change > 0;
                
                return (
                  <div
                    key={alert._id}
                    onClick={() => { navigate(`/deal/${alert.deal_id}`); setShowDropdown(false); }}
                    className="p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        isPriceDown ? 'bg-green-100' : 'bg-blue-100'
                      }`}>
                        <TrendingDown className={`w-4 h-4 ${isPriceDown ? 'text-green-600' : 'text-blue-600'}`} />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900 line-clamp-1">
                          {alert.product_title}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="text-sm font-bold text-gray-900">
                            ${alert.current_price?.toFixed(2) || 'N/A'}
                          </p>
                          {isPriceDown && (
                            <span className="text-xs font-semibold text-green-600 flex items-center gap-1">
                              ↓ ${Math.abs(alert.price_change).toFixed(2)} off
                            </span>
                          )}
                          {isPriceUp && (
                            <span className="text-xs font-semibold text-red-600 flex items-center gap-1">
                              ↑ ${Math.abs(alert.price_change).toFixed(2)}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Click to view deal</p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <div className="p-3 border-t border-gray-200">
            <button
              onClick={() => { navigate('/preferences'); setShowDropdown(false); }}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Manage Preferences →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
